<template>
  <v-container fluid class="account-page pa-0">
    <!-- || Subscription && Subscription.status === 'incomplete_expired' -->
    <div v-if="
      (currentBusiness && !currentBusiness.subscription_id) ||
      (Subscription && Subscription.status === 'incomplete_expired')
    ">
      <div class="text-center">
        <h2 class="title">Welcome</h2>
        <small>Setup subscription</small>
        <v-row class="mt-5">
          <v-col cols="12">
            <h2 class="title mb-2">Enter a payment method</h2>
          </v-col>
          <v-col cols="12" align="center">
            <v-card outlined flat width="500" :loading="isLoading">
              <v-card-text>
                <card ref="card" class="stripe-card mt-7" :class="{ complete }" :stripe="stripePublicKey"
                  @change="complete = $event.complete" />

                <v-img src="@/assets/images/stripeimg.jpg" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <v-col class="mt-3" align="center">
        <v-row cols="12" justify="center">
          <h2 class="title mb-2">Select Plan</h2>
        </v-row>
        <v-col cols="12">
          <v-card outlined flat width="500" :loading="isLoading">
            <v-card-text>
              <v-radio-group v-model="selectedPlan" column>
                <v-radio :value="plan" :key="index" v-for="(plan, index) in plans">
                  <template v-slot:label>
                    <div>
                      <div class="title" v-if="plan.nickname">{{ plan.nickname }}</div>
                      <div>{{ (plan.amount / 100) | currency }}</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>
        </v-col>

        <v-btn v-if="selectedPlan" color="success" class="mt-3" :loading="btnLoading" :disabled="!complete" rounded
          large @click="submit">{{ (selectedPlan.amount / 100) | currency }} Charge</v-btn>
      </v-col>
      <v-col class="mt-3" align="center">
        <v-row cols="12" justify="center">
          <v-btn color="primary" to="/logout">{{
            $t("buttons.logout")
          }}</v-btn>
        </v-row>
      </v-col>
    </div>

    <div v-else class="account-shell">
      <div class="account-header">
        <div>
          <div class="page-title">My Account</div>
          <div class="page-subtitle">Manage your smplsale subscription and billing information.</div>
        </div>
        <v-chip v-if="Subscription" small :class="['subscription-status', 'status-' + Subscription.status]">
          <span class="status-dot"></span>{{ statusLabel }}
        </v-chip>
      </div>

      <v-card outlined :loading="isLoading" class="billing-card">
        <div v-if="Subscription && Subscription.status === 'canceled'" class="restart-banner">
          <div class="restart-icon"><v-icon color="red darken-1">mdi-refresh-circle</v-icon></div>
          <div class="restart-copy">
            <div class="restart-title">Your subscription is canceled</div>
            <div class="restart-message">Restart the same subscription with the same plan and recurring amount you had before.</div>
          </div>
          <v-btn v-if="canManageSubscription" color="primary" rounded depressed :loading="restartLoading" @click="restartSubscription">
            <v-icon left small>mdi-refresh</v-icon>Restart my subscription
          </v-btn>
        </div>

        <div v-if="Subscription" class="billing-hero">
          <div>
            <div class="eyebrow">CURRENT SUBSCRIPTION</div>
            <div class="plan-name">{{ planName }}</div>
            <div class="plan-note">Your complete smplsale service in one recurring bill.</div>
          </div>
          <div class="price-wrap">
            <div class="price">{{ formatStripeAmount(recurringAmount) }}</div>
            <div class="price-interval">{{ billingInterval }}</div>
          </div>
        </div>

        <div v-if="Subscription" class="billing-facts">
          <div class="fact-card">
            <div class="fact-icon blue-soft"><v-icon color="primary">mdi-calendar-clock</v-icon></div>
            <div><div class="fact-label">Next billing date</div><div class="fact-value">{{ nextBillingDate }}</div></div>
          </div>
          <div class="fact-card">
            <div class="fact-icon green-soft"><v-icon color="green darken-2">mdi-receipt-text-check-outline</v-icon></div>
            <div><div class="fact-label">Next bill</div><div class="fact-value">{{ formatStripeAmount(nextInvoiceAmount) }}</div></div>
          </div>
        </div>

        <div class="section-divider"></div>

        <div class="payment-section">
          <div class="section-heading">
            <div><div class="section-title">Payment method</div><div class="page-subtitle">This card will be used for your recurring subscription.</div></div>
            <AddCard :sources="sources" :buttonLabel="defaultCard ? 'Update card' : 'Add payment method'" @submit="getCustomer()" />
          </div>

          <div v-if="defaultCard" class="card-on-file">
            <div class="card-brand-icon"><v-icon color="primary">mdi-credit-card-outline</v-icon></div>
            <div class="card-details">
              <div class="card-brand">{{ defaultCard.brand }} ending in {{ defaultCard.last4 }}</div>
              <div class="page-subtitle">Expires {{ padMonth(defaultCard.exp_month) }}/{{ defaultCard.exp_year }}</div>
            </div>
            <v-chip x-small color="green lighten-5" text-color="green darken-3">Default</v-chip>
          </div>
          <div v-else class="no-card"><v-icon color="orange" class="mr-2">mdi-alert-circle-outline</v-icon>No payment method is currently on file.</div>
        </div>

        <div class="section-divider"></div>

        <div class="history-section">
          <div class="section-title mb-3">Billing history</div>
          <InvoiceList />
        </div>

        <div v-if="Subscription && Subscription.status !== 'canceled'" class="danger-zone">
          <div><div class="danger-title">Cancel subscription</div><div class="page-subtitle">Your access to paid services will be affected.</div></div>
          <v-btn text rounded small color="red darken-1" @click="cancelSubscrition">Cancel Subscription</v-btn>
        </div>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import AddCard from "./AddCard";
import InvoiceList from "./InvoiceList";
import { Card, createToken } from "vue-stripe-elements-plus";
import accountService from "@/modules/Account/service.js";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "account-home",
  data() {
    return {
      sources: [],
      btnLoading: false,
      selectedPlan: null,
      Customer: null,
      Subscription: null,
      isLoading: true,
      cards: [],
      complete: false,
      stripePublicKey: process.env.VUE_APP_STRIPE_PK,
      stripe: null,
      plans: null,
      restartLoading: false,
    };
  },
  async created() {
    await this.initUser();
    await this.getCustomer();
    await this.getPlans();
    if (this.currentBusiness.subscription_id) await this.getSubscription();
    await this.setPlaninit();
    this.isLoading = false;
  },
  components: {
    AddCard,
    Card,
    InvoiceList,
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    color() {
      let color = "gray";
      if (!this.Subscription) return color;
      if (this.Subscription.status === "active") color = "blue lighten-5";
      if (this.Subscription.status === "canceled")
        color = "deep-orange lighten-1";
      if (this.Subscription.status === "past_due") color = "yellow lighten-2";
      if (this.Subscription.status === "unpaid") color = "orange lighten-2";

      return color;
    },
    billingSummary() {
      return (this.Subscription && this.Subscription.billingSummary) || {};
    },
    subscriptionItems() {
      return this.Subscription && this.Subscription.items && this.Subscription.items.data
        ? this.Subscription.items.data
        : [];
    },
    recurringAmount() {
      if (this.billingSummary.recurringAmount !== undefined) return Number(this.billingSummary.recurringAmount);
      return this.subscriptionItems.reduce((total, item) => total + Number((item.plan && item.plan.amount) || 0) * Number(item.quantity || 1), 0);
    },
    nextInvoiceAmount() {
      return this.billingSummary.nextInvoiceAmount !== undefined
        ? Number(this.billingSummary.nextInvoiceAmount)
        : this.recurringAmount;
    },
    planName() {
      if (this.billingSummary.planName) return this.billingSummary.planName;
      if (this.Subscription && this.Subscription.plan && this.Subscription.plan.nickname) return this.Subscription.plan.nickname;
      if (this.subscriptionItems.length === 1 && this.subscriptionItems[0].plan) return this.subscriptionItems[0].plan.nickname || "smplsale subscription";
      return "smplsale subscription";
    },
    billingInterval() {
      const interval = this.billingSummary.interval || (this.Subscription && this.Subscription.plan && this.Subscription.plan.interval);
      const count = Number(this.billingSummary.intervalCount || 1);
      if (!interval) return "recurring";
      return count > 1 ? `every ${count} ${interval}s` : `per ${interval}`;
    },
    nextBillingDate() {
      const timestamp = this.billingSummary.nextBillingAt || (this.Subscription && this.Subscription.current_period_end);
      return timestamp ? this.$moment.unix(timestamp).format("MMMM D, YYYY") : "Not scheduled";
    },
    defaultCard() {
      if (!this.sources.length) return null;
      if (this.Customer && this.Customer.default_source) {
        return this.sources.find(card => card.id === this.Customer.default_source) || this.sources[0];
      }
      return this.sources[0];
    },
    statusLabel() {
      if (!this.Subscription || !this.Subscription.status) return "Unknown";
      return this.Subscription.status.replace(/_/g, " ");
    },
    canManageSubscription() {
      return !!(this.currentUser && (this.currentUser.isAdmin || this.currentUser.superAdmin));
    },
  },
  methods: {
    ...mapActions("global", ["initUser", "initBusiness"]),
    formatStripeAmount(amount) {
      const currency = this.billingSummary.currency || "usd";
      return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(Number(amount || 0) / 100);
    },
    padMonth(month) {
      return String(month || "").padStart(2, "0");
    },
    async restartSubscription() {
      const confirmation = await this.$swal({
        title: "Restart subscription?",
        text: "Your previous plan and recurring charges will be restarted using the payment method on file.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Restart Subscription",
        cancelButtonText: "Not now",
      });
      if (!confirmation.value) return;

      this.restartLoading = true;
      try {
        const result = await accountService.restartSubscription();
        const data = result.data || {};
        if (data.business) await this.initBusiness(data.business);
        if (data.subscription) this.Subscription = data.subscription;
        await this.getSubscription();
        const key = `${process.env.VUE_APP_STORAGE_PREFIX || "smplsale-"}canceled-subscription-dismissals-${this.currentBusiness.id || this.currentBusiness.code}`;
        localStorage.removeItem(key);
        this.$swal("Subscription restarted", "Your account subscription is active again.", "success");
      } catch (error) {
        const message = error && error.data && error.data.message
          ? error.data.message
          : (error.message || "Unable to restart the subscription. Please review your payment method and try again.");
        this.$swal("Unable to restart", message, "error");
      } finally {
        this.restartLoading = false;
      }
    },
    setPlaninit() {
      (this.plans || []).map((plan) => {
        if (plan.nickname === "Quarterly") this.selectedPlan = plan;
      });
    },
    getPlans() {
      this.isLoading = true;
      return accountService.getPlans().then((result) => {
        console.log('result get plan', result)
        this.plans = result.data;
        this.isLoading = false;
      });
    },
    async submit() {
      // console.log("in submit");
      try {
        this.btnLoading = true;
        let customer_id = this.currentBusiness.customer_id;
        if (!this.currentBusiness.customer_id) {
          const customer = await accountService.createCustomer({
            email: this.currentBusiness.email,
            name: this.currentBusiness.name,
            phone: this.currentBusiness.phoneNumber,
            description: `Account Code: ${this.currentBusiness.code}`,
            metadata: {
              accountCode: this.currentBusiness.code,
              phoneNumber: this.currentBusiness.phoneNumber,
            },
          });
          customer_id = customer.id;
        }
        // console.log("customer_id", customer_id);
        const data = await createToken();
        await accountService.addCard({
          card: {
            customer_id: customer_id,
            source: data.token.id,
          },
        });

        await accountService.createSubscrition({
          customer_id: customer_id,
          plan_id: this.selectedPlan.id,
        });

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Subscrition Start",
          });
        this.btnLoading = false;
        this.$router.push("/");
      } catch (error) {
        // console.log("error adding card", error);
        this.btnLoading = false;
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: error.message,
          });
      }
    },
    cancelSubscrition() {
      this.$swal({
        title: "Are you sure?",
        text: "You can to cancel you subscription",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.isLoading = true;
          return accountService
            .cancelSubscrition({
              subscription_id: this.currentBusiness.subscription_id,
            })
            .then(() => {
              // console.log("result", result);
              this.initUser();
              this.getSubscription();
              this.isLoading = false;
            });
        }
      });
    },
    getSubscription() {
      return accountService
        .getSubscription({
          subscription_id: this.currentBusiness.subscription_id,
        })
        .then((result) => {
          console.log("asdfksdfsdf", result);
          this.Subscription = result.data;
        });
    },
    getCustomer() {
      if (!this.currentBusiness.customer_id) {
        this.Customer = null;
        this.sources = [];
        return Promise.resolve();
      }
      return accountService
        .getCustomer({ customer_id: this.currentBusiness.customer_id })
        .then((result) => {
          // console.log("result customer", result);
          this.Customer = result.data;
          this.sources = this.Customer && this.Customer.sources ? this.Customer.sources.data || [] : [];
        });
    },
  },
};
</script>

<style scoped>
.account-page{background:#f8fafc;min-height:100%}.account-shell{max-width:1050px;margin:0 auto}.account-header{padding:18px 4px;display:flex;align-items:center;justify-content:space-between;gap:16px}.page-title{font-size:24px;font-weight:800;color:#0f172a}.page-subtitle{font-size:12px;color:#64748b;margin-top:3px}.billing-card{border-radius:22px!important;overflow:hidden;background:#fff}.billing-hero{padding:28px;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;background:linear-gradient(135deg,#fff 30%,#f1f6ff 100%)}.eyebrow{font-size:10px;font-weight:800;letter-spacing:.14em;color:#64748b}.plan-name{font-size:25px;font-weight:800;color:#0f172a;margin-top:5px;text-transform:capitalize}.plan-note{font-size:13px;color:#64748b;margin-top:5px}.price-wrap{text-align:right}.price{font-size:34px;font-weight:900;color:#0f172a;line-height:1}.price-interval{font-size:12px;color:#64748b;margin-top:6px}.subscription-status{font-weight:800;text-transform:capitalize}.status-dot{width:7px;height:7px;border-radius:50%;background:currentColor;margin-right:7px}.status-active,.status-trialing{background:#ecfdf3!important;color:#15803d!important}.status-past_due,.status-unpaid{background:#fff7ed!important;color:#c2410c!important}.status-canceled,.status-incomplete_expired{background:#fff1f2!important;color:#be123c!important}.billing-facts{padding:0 28px 24px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}.fact-card{padding:15px;background:#f8fafc;border-radius:16px;display:flex;align-items:center;gap:12px}.fact-icon{width:42px;height:42px;border-radius:13px;display:flex;align-items:center;justify-content:center}.blue-soft{background:#eaf2ff}.green-soft{background:#e9f8ef}.fact-label{font-size:11px;color:#64748b}.fact-value{font-size:17px;font-weight:800;color:#0f172a}.section-divider{height:1px;background:#e2e8f0}.payment-section,.history-section{padding:24px 28px}.section-heading{display:flex;align-items:center;justify-content:space-between;gap:16px}.section-title{font-size:17px;font-weight:800;color:#0f172a}.card-on-file{margin-top:16px;max-width:480px;padding:14px;border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;display:flex;align-items:center}.card-brand-icon{width:44px;height:44px;border-radius:13px;background:#eaf2ff;display:flex;align-items:center;justify-content:center;margin-right:12px}.card-details{flex:1}.card-brand{font-size:14px;font-weight:800;color:#334155;text-transform:capitalize}.no-card{margin-top:16px;padding:14px;border-radius:14px;background:#fff7ed;color:#9a3412;display:flex;align-items:center}.danger-zone{padding:18px 28px;background:#fffafa;border-top:1px solid #fee2e2;display:flex;align-items:center;justify-content:space-between;gap:16px}.danger-title{font-size:13px;font-weight:800;color:#991b1b}@media(max-width:600px){.account-header,.billing-hero,.section-heading,.danger-zone{align-items:flex-start;flex-direction:column}.price-wrap{text-align:left}.billing-facts{grid-template-columns:1fr;padding-left:16px;padding-right:16px}.payment-section,.history-section,.billing-hero{padding-left:16px;padding-right:16px}}
.restart-banner{margin:20px 20px 0;padding:16px;border:1px solid #fecdd3;border-radius:17px;background:#fff7f8;display:flex;align-items:center;gap:13px}.restart-icon{width:46px;height:46px;border-radius:14px;background:#ffe4e6;display:flex;align-items:center;justify-content:center;flex:none}.restart-copy{flex:1}.restart-title{font-size:15px;font-weight:800;color:#9f1239}.restart-message{font-size:12px;color:#64748b;margin-top:3px;line-height:1.45}@media(max-width:700px){.restart-banner{align-items:flex-start;flex-direction:column}.restart-banner .v-btn{width:100%}}
</style>
